/**
 * @packageDocumentation
 * @module HackerAPI.Event
 */
import { __extends } from "tslib";
import HackerAPIFetch from "../hackerapi-fetch/hackerapi-fetch";
import Attendee from "./attendee/attendee";
import BadgeCode from "./badge-code/badge-code";
import MailingListSignup from "./mailing-list-signup/mailing-list-signup";
import Team from "./team/team";
import Update from "./update/update";
import Faq from "./faq/faq";
import ScheduleItem from "./schedule-item/schedule-item";
import Activity from "./activity/activity";
import ActivityRedemption from "./activity-redemption/activity-redemption";
import Sponsor from "./sponsor/sponsor";
import Submission from "./submission/submission";
import Application from "./application/application";
import Base from "../base/base";
import { responseToDataUri, parseDate } from "../utils/utils";
import Organization from "../organization/organization";
import Pipeline from "../pipeline/pipeline";
/**
 * An `Event` is a container for general info about an event. It also provides static methods for
 * creating/updating/reading/deleting things related to an event on hackerapi.com
 * @class
 * @extends { HackerAPI.Base }
 */
var Event = /** @class */ (function (_super) {
    __extends(Event, _super);
    /**
     * Creates a local Event instance.
     * @constructor
     * @param event - Data about the event
     */
    function Event(_a) {
        var _b = _a === void 0 ? {} : _a, id = _b.id, slug = _b.slug, name = _b.name, organization = _b.organization, description = _b.description, start_time = _b.start_time, startTime = _b.startTime, end_time = _b.end_time, endTime = _b.endTime, size = _b.size, length_hours = _b.length_hours, lengthHours = _b.lengthHours, contact_email = _b.contact_email, contactEmail = _b.contactEmail, website = _b.website, timezone = _b.timezone, cost = _b.cost, has_reimbursements = _b.has_reimbursements, hasReimbursements = _b.hasReimbursements, has_food = _b.has_food, hasFood = _b.hasFood, tags = _b.tags, swarm_event_id = _b.swarm_event_id, swarmEventId = _b.swarmEventId, min_age_restriction = _b.min_age_restriction, minAgeRestriction = _b.minAgeRestriction, max_age_restriction = _b.max_age_restriction, maxAgeRestriction = _b.maxAgeRestriction, cost_currency = _b.cost_currency, costCurrency = _b.costCurrency, judging_model = _b.judging_model, judgingModel = _b.judgingModel, submission_url = _b.submission_url, submissionUrl = _b.submissionUrl, themes = _b.themes, pipelines = _b.pipelines, created_at = _b.created_at, createdAt = _b.createdAt, updated_at = _b.updated_at, updatedAt = _b.updatedAt;
        var _this = _super.call(this, {
            organization: function (k, v) { return ["organization_id", v.id]; },
            startTime: Base.dateTimeHandler,
            endTime: Base.dateTimeHandler
        }) || this;
        /**
         * The ID of the event
         * @type { number }
         */
        _this.id = id;
        /**
         * The slug name of the event. The slug is used to denote the event in API calls.
         * @type { string }
         */
        _this.slug = slug;
        /**
         * The name of the event
         * @type { string }
         */
        _this.name = name;
        /**
         * Information about the organization running the event
         * @type { HackerAPI.Organization }
         */
        _this.organization = new Organization(organization);
        /**
         * A description of the event
         * @type { string }
         */
        _this.description = description;
        /**
         * The start time of the event
         * @type { Date }
         */
        _this.startTime = parseDate(start_time || startTime);
        /**
         * The end time of the event
         * @type { Date }
         */
        _this.endTime = parseDate(end_time || endTime);
        /**
         * The number of people attending the event
         * @type { number }
         */
        _this.size = size;
        /**
         * How long the event runs for in hours
         * @type { number }
         */
        _this.lengthHours = length_hours || lengthHours;
        /**
         * The email to contact for info about the event
         * @type { string }
         */
        _this.contactEmail = contact_email || contactEmail;
        /**
         * The website of the event
         * @type { string }
         */
        _this.website = website;
        /**
         * The timezone of the event
         * @type { string }
         */
        _this.timezone = timezone;
        /**
         * The cost to attend the event
         * @type { string }
         */
        _this.cost = cost;
        /**
         * Whether or not the event provides reimbursements
         * @type { boolean }
         */
        _this.hasReimbursements = has_reimbursements || hasReimbursements;
        /**
         * Whether or not the event has food
         * @type { boolean }
         */
        _this.hasFood = has_food || hasFood;
        /**
         * Tags about the event
         * @type { string[] }
         */
        _this.tags = tags;
        /**
         * The swarm ID of the event
         * @type { string }
         */
        _this.swarmEventId = swarm_event_id || swarmEventId;
        /**
         * The minimum age you can be to attend the event
         * @type { number }
         */
        _this.minAgeRestriction = min_age_restriction || minAgeRestriction;
        /**
         * The maximum age you can be to attend the event
         * @type { number }
         */
        _this.maxAgeRestriction = max_age_restriction || maxAgeRestriction;
        /**
         * The currency that `this.cost` is expressed in
         * @type { string }
         */
        _this.costCurrency = cost_currency || costCurrency;
        /**
         * The model used for judging
         * @type { string }
         */
        _this.judgingModel = judging_model || judgingModel;
        /**
         * The URL to submit at
         * @type { string }
         */
        _this.submissionUrl = submission_url || submissionUrl;
        /**
         * Themes of the event
         * @type { string }
         */
        _this.themes = themes;
        /**
         * The pipelines related to the event
         * @todo Check up on type when pipelines are implemented
         * @type { HackerAPI.Pipeline[] }
         */
        _this.pipelines = (pipelines || []).map(function (p) { return new Pipeline(p); });
        /**
         * When the event was created
         * @type { Date }
         */
        _this.createdAt = parseDate(created_at || createdAt);
        /**
         * When the event was last updated
         * @type { Date }
         */
        _this.updatedAt = parseDate(updated_at || updatedAt);
        return _this;
    }
    Object.defineProperty(Event, "Activity", {
        /**
         * @returns
         */
        get: function () {
            return Activity;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "ActivityRedemption", {
        /**
         * @returns
         */
        get: function () {
            return ActivityRedemption;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Application", {
        /**
         * @returns
         */
        get: function () {
            return Application;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Attendee", {
        /**
         * @returns
         */
        get: function () {
            return Attendee;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "BadgeCode", {
        get: function () {
            return BadgeCode;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Faq", {
        /**
         * @returns
         */
        get: function () {
            return Faq;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "MailingListSignup", {
        /**
         * @returns
         */
        get: function () {
            return MailingListSignup;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "ScheduleItem", {
        /**
         * @returns
         */
        get: function () {
            return ScheduleItem;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Sponsor", {
        /**
         * @returns
         */
        get: function () {
            return Sponsor;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Submission", {
        /**
         * @returns
         */
        get: function () {
            return Submission;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Team", {
        /**
         * @returns
         */
        get: function () {
            return Team;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event, "Update", {
        /**
         * @returns
         */
        get: function () {
            return Update;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Pulls data about an event using it's slug
     * @param - The event to get based on the slug
     * @param token - The credentials to use to get the data (must use an organizer token)
     * @returns - Returns itself
     * @example
     * let lollahacks;
     * HackerAPI.Event.load({ slug: "lollahacks" })
     * .then((e) => {
     *   lollahacks = e;
     * })
     */
    Event.fetch = function (_a, token) {
        var slug = _a.slug;
        return HackerAPIFetch({
            path: "/v2/events/" + slug,
            method: "GET",
            token: token
        }).then(function (data) { return new Event(data); });
    };
    /**
     * Updates event data on HackerAPI of the given event using the event's slug.
     * @param - The event to update (can also contain a `token` attribute)
     * @param - The credentials to use to get the data (must use an organizer token)
     * @returns The updated event
     * @example
     * HackerAPI.Event.load({ slug: "lollahacks" })
     * .then((event) => {
     *   event.description = "...";
     *   HackerAPI.Event.update(event)
     *   .then((updatedEvent) => {
     *     doSomething(updatedEvent);
     *   })
     * })
     */
    Event.update = function (event, token) {
        return HackerAPIFetch({
            path: "/v2/events/" + event.slug,
            method: "PUT",
            token: token,
            body: event.toHackerAPIFormat()
        }).then(function (data) { return new Event(data); });
    };
    /**
     * Creates an event on HackerAPI of this event using the event's slug.<br>
     * The following properties are required:<br>
     * - `organizationID`
     * - `name`
     * - `startTime`
     * - `endTime`
     * - `slug`
     * @param event - The event to create (can also contain the token to use)
     * @param token - The credentials to use to get the data (must use an organizer token)
     * @returns - Returns an updated copy of the event
     * @example
     * let masseyhacks4 = new Event();
     * fillUp(masseyhacks4);
     * HackerAPI.Event.create(masseyhacks, myToken)
     * .then((event) => {
     *   doSomethingWith(event)
     * })
     */
    Event.create = function (event, token) {
        return HackerAPIFetch({
            path: "/v2/events",
            method: "POST",
            token: token,
            body: event.toHackerAPIFormat()
        }).then(function (data) { return new Event(data); });
    };
    // ------------------------------------------- SLACK ------------------------------------------------
    /**
     * Adds a user to Slack
     * @todo Is there maybe a better location for this?
     * @param event - The event (which seems to be required but does nothing)
     * @param event.slug
     * @param user - The user to add to slack
     * @param user.id
     * @param token
     * @returns
     */
    Event.addUserToSlack = function (_a, _b, token) {
        var slug = _a.slug;
        var id = _b.id;
        return HackerAPIFetch({
            path: "/v2/events/" + slug + "/slack",
            method: "POST",
            params: {
                as_user: id
            },
            token: token
        });
    };
    // --------------------------------------------- BADGE -----------------------------------------------
    /**
     * Gets a badge image for a person given their information
     * @param event - Doesn't actually do anything really (for now?)
     * @param params
     * @param name - The name to go on the badge
     * @param role - The person's role
     * @param validUntil - The number of hours this badge is valid for
     * @param company - What company this person works for
     * @returns A data URI of the badge
     */
    Event.getBadge = function (_a, _b) {
        var slug = _a.slug;
        var name = _b.name, role = _b.role, validUntil = _b.validUntil, company = _b.company;
        return HackerAPIFetch({
            path: "/v2/events/" + slug + "/badge",
            method: "GET",
            responseType: "arraybuffer",
            fullResponse: true,
            params: {
                name: name,
                company: company,
                role: role,
                valid_until: validUntil
            }
        }).then(responseToDataUri);
    };
    // ---------------------------------------- MENTOR NOTIFICATIONS --------------------------------------
    /**
     * This feature doesn't seem to be fully implemented (or functional?) in hackerapi so use at your own discretion
     * @param event
     * @param event.slug
     * @param mentorRequest
     * @param mentorRequest.action - So far looks like this can only be "claimed"
     * @param mentorRequest.skills - The skills to filter mentors by
     * @param user
     * @param user.token
     * @returns No idea what that object is
     */
    Event.pushMentorNotification = function (_a, mentorRequest, token) {
        var slug = _a.slug;
        return HackerAPIFetch({
            path: "/v2/events/" + slug + "/mentor-notifications",
            method: "POST",
            body: mentorRequest,
            token: token
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        });
    };
    // ------------------------------------------- UPLOAD --------------------------------------------------
    /**
     * Uploads a file
     * @param event
     * @param event.slug
     * @param formData
     * @param [token]
     * @param [token.token]
     */
    Event.upload = function (_a, formData, token) {
        var slug = _a.slug;
        return HackerAPIFetch({
            path: "/v2/events/" + slug + "/upload",
            method: "POST",
            token: token,
            body: formData
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        });
    };
    return Event;
}(Base));
export default Event;
//# sourceMappingURL=event.js.map